import MVT from "ol/format/MVT.js";
import VectorTileLayer, { type Options as VectorTileLayerOptions } from "ol/layer/VectorTile";
import type * as olPixel from "ol/pixel";
import { fromLonLat } from "ol/proj";
import type RenderFeature from "ol/render/Feature";
import VectorTileSource from "ol/source/VectorTile.js";
import type { Rule } from "ol/style/flat";
import type Tile from "ol/Tile";
import type VectorTile from "ol/VectorTile.js";
import { getAPIBaseUrl, getJwtToken } from "./environment";
import {
  detourStyle,
  hikingActivityStyle,
  hikingPropositionStyle,
  hikingStyle,
  poiStyle,
  trackLineHover,
} from "./style/mvt-layers";

const layerNames = ["hiking", "hiking_proposition", "hiking_activity", "poi", "detours"] as const;
type LayerName = (typeof layerNames)[number];

class MVTSource extends VectorTileSource<RenderFeature> {
  constructor(layer: LayerName) {
    super({
      url: `${getAPIBaseUrl()}/${layer}/{z}/{x}/{y}.pbf`,
      format: new MVT(),
      tileSize: 512,
      maxZoom: 14,
      tileLoadFunction: (tile: Tile, url: string) => this.fetchTile(tile as VectorTile, url),
    });
    this.set("layer", layer);
  }

  private async fetchTile(tile: VectorTile, url: string) {
    const layer = this.get("layer");
    const org = this.get("organisation");
    const uuids = this.get("uuids");
    const poi_types = this.get("poi_types");
    const max_size_param = this.get("max_size_param");
    tile.setLoader(async (extent, _resolution, projection) => {
      const headers: HeadersInit = {};
      // Only hiking data needs a JWT token
      if (layer === "hiking") {
        headers.Authorization = `Bearer ${getJwtToken()}`;
      }

      const params = new URLSearchParams();
      if (org) params.append("org", org);
      if (uuids) params.append("uuids", uuids);
      if (poi_types) params.append("poi_types", poi_types);
      if (max_size_param) params.append("max_size_param", max_size_param);

      const response = await fetch(`${url}?${params.toString()}`, {
        headers: headers,
      });

      if (!response.ok) {
        return;
      }
      const data = await response.arrayBuffer();
      const features = tile.getFormat().readFeatures(data, {
        extent: extent,
        featureProjection: projection,
      });
      tile.setFeatures(features);
    });
  }
}

const styleByLayer: Record<LayerName, Rule[]> = {
  hiking: hikingStyle,
  hiking_proposition: hikingPropositionStyle,
  hiking_activity: hikingActivityStyle,
  poi: poiStyle,
  detours: detourStyle,
};

const minZoomByLayer: Record<LayerName, number> = {
  hiking: Number.NEGATIVE_INFINITY,
  hiking_proposition: Number.NEGATIVE_INFINITY,
  hiking_activity: Number.NEGATIVE_INFINITY,
  poi: 11,
  detours: 12,
};

type MVTLayerOptions = Omit<VectorTileLayerOptions<RenderFeature>, "source" | "style" | "minZoom"> & {
  layer: LayerName;
  organisation?: string;
  uuids?: string;
  poi_types?: string;
  max_size_param?: number;
};

/**
 * # MVTLayer
 *
 * ## Demos
 *  - [Display mvt layers into the map](/demo/mvt.html)
 *
 * ## Description
 *
 * The MVTLayer is an OpenLayers layer that loads tile data from hiking, hiking proposition, hiking activity, points of interest or detours.
 *
 * Before using the MVTLayer, you need to set the environment and the jwt token {@link environment} if needed.
 *
 * Adding all the hiking of the user to the map:
 * ```js
 * // Create a mvt layer for hiking
 * const layer = new sww.MVTLayer({
 *   layer: "hiking"
 * });
 *
 * // Add it to the map
 * map.addLayer(layer);
 * ```
 *
 * For the other layers, you can use the same way only changing the layer name:
 *  - Hiking proposition: `hiking_proposition`
 *  - Hiking activity: `hiking_activity`
 *  - Points of interest: `poi`
 *  - Detours: `detours`
 *
 * ## Authentication
 *
 * For hiking data, it uses a JWT token to load only user's specific data. For the other layers, no token is needed.
 * No special `scope` is needed for the token, only the `sub` value is used to load the user's data.
 *
 * ## Organisation and Uuids filters
 *
 * For the hiking proposition and activity , the data can be filtered by organisation or uuids by passing the corresponding option:
 * ```js
 * // Create a mvt layer for hiking
 * const layer = new sww.MVTLayer({
 *  layer: "hiking_activity",
 *  organisation: "Jura Rando",
 *  uuids: "uuid1, uuid2"
 * });
 * ```
 * The uuids option is a string of uuids separated by a commas.
 * Without the organisation option, the layer will display all the data.
 *
 * ## POI type filter
 *
 * For the poi layer, the data can be filtered by type by passing the 'poi_types' option:
 * ```js
 * // Create a mvt layer for hiking
 * const layer = new sww.MVTLayer({
 *  layer: "poi",
 *  poi_types: "type1, type2"
 * });
 * ```
 * The poi_types option is a string of types separated by a commas.
 *
 * ## Feature Highlighting
 *
 * You can highlight LineStrings and MultiLineStrings on the map by calling the `highlightAtPixel` method with the pixel array and the type of event (hover or click).
 * The highlights for hover and click events are managed independent, so you can have a persistent highlight for a clicked feature and a temporary highlight for a hovered feature.
 * The highlight clears when there is no feature at the specified pixel.
 *
 * ```js
 * // Highlight a specific feature
 * layer.highlightAtPixel([100, 100], "click");
 *```
 *
 * Example of usage with a hover event on the map:
 *
 * ```js
 * const mvtLayers = ["poi", "hiking_proposition", "hiking_activity", "hiking", "detours"];
 * map.on("pointermove", (e) => {
 *   const pixel = e.map.getEventPixel(e.originalEvent);
 *   e.map.getLayers().forEach((layer) => {
 *     if (mvtLayers.includes(layer.get("layer"))) {
 *       layer.highlightAtPixel(pixel, "hover");
 *     }
 *   });
 * });
 * ``` */
export default class MVTLayer extends VectorTileLayer<RenderFeature> {
  private highlightIds: Record<string, string> = { click: "", hover: "" };
  constructor(options: MVTLayerOptions) {
    const { layer, organisation, uuids, poi_types, max_size_param, ...vectorTileLayerOptions } = options;

    if (!layerNames.includes(layer)) {
      throw new Error(`Invalid layer name: ${layer}`);
    }

    super(vectorTileLayerOptions);

    // only request inside the Switzerland extent
    this.setExtent([...fromLonLat([5.140242, 45.398181]), ...fromLonLat([11.47757, 48.230651])]);
    this.setStyle(styleByLayer[layer]);
    this.setMinZoom(minZoomByLayer[layer]);

    const source = new MVTSource(layer);
    source.set("organisation", organisation);
    source.set("uuids", uuids);
    source.set("poi_types", poi_types);
    source.set("max_size_param", max_size_param);
    this.setSource(source);

    this.set("layer", layer);
  }

  /**
   * Highlights features (LineStrings and MultiLineStrings) at the specified pixel location
   * @param pixel - The pixel coordinates where to look for features
   * @param type - The type of highlight to apply: 'hover' or 'click'
   */
  async highlightAtPixel(pixel: olPixel.Pixel, type: "hover" | "click"): Promise<void> {
    const features = (await this.getFeatures(pixel)).filter((f) => {
      const geomType = f.getGeometry()?.getType();
      return geomType === "LineString" || geomType === "MultiLineString";
    });

    if (features.length === 0) {
      if (this.highlightIds[type] !== "") {
        this.highlightIds[type] = "";
        this.updateStyles();
      }
    } else {
      const featureId = features[0].get("uuid");
      if (this.highlightIds[type] !== featureId) {
        this.highlightIds[type] = featureId;
        this.updateStyles();
      }
    }
  }

  private updateStyles() {
    const layer = this.get("layer") as LayerName;
    const style = [
      {
        filter: [
          "all",
          ["==", ["get", "layer"], `${layer}_lines`],
          ["any", ["==", ["get", "uuid"], this.highlightIds.click], ["==", ["get", "uuid"], this.highlightIds.hover]],
        ],
        style: trackLineHover,
      },
      ...styleByLayer[layer],
    ];
    this.setStyle(style);
  }
}
